/**
 * « + Article reçu en plus » (14/10/2026) — un article livré qui n'est pas dans
 * le document importé. Cas réel : le BL papier Hygiena HY26011101 portait un
 * reliquat d'une commande d'août (LAVACH Sauce frites bio 200 ml, réf. 050711),
 * absent de la confirmation de commande importée.
 *
 * La ligne s'ajoute « en attente » à la réception ; on la rattache ensuite à
 * une fiche (ou on crée la fiche) et on la valide comme les autres lignes.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';

interface Props {
  deliveryId: string;
  onClose: () => void;
  onAjoute: () => void;
}

const nombre = (v: string) => Number(v.replace(',', '.').trim());

export default function ArticleEnPlusModal({ deliveryId, onClose, onAjoute }: Props) {
  const [nom, setNom] = useState('');
  const [quantite, setQuantite] = useState('');
  const [prix, setPrix] = useState('');
  const [tva, setTva] = useState(6);
  const [ean, setEan] = useState('');
  const [reference, setReference] = useState('');
  const [dluo, setDluo] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const q = nombre(quantite);
  const p = prix.trim() ? nombre(prix) : null;
  const valide = nom.trim().length >= 2 && q > 0 && (p === null || p >= 0);

  const ajouter = async () => {
    if (!valide) return;
    setEnvoi(true);
    try {
      await api.post(`/deliveries/${deliveryId}/lines`, {
        name: nom.trim(),
        quantity: q,
        unit_price_ht_eur: p,
        tva,
        barcode: ean.trim() || null,
        product_code: reference.trim() || null,
        expiry_date: dluo || null,
      });
      toast.success(`${nom.trim()} ajouté à la réception : rattachez-le à une fiche puis validez-le`);
      onAjoute();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ajout impossible');
      setEnvoi(false);
    }
  };

  const champ = 'w-full border border-gray-300 rounded-lg px-3 py-3 text-base';
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden">
        <div className="bg-emerald-700 px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">+ Article reçu en plus</h2>
            <p className="text-emerald-100 text-sm">Livré, mais absent du document importé (reliquat, ajout…)</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-3xl leading-none" aria-label="Fermer">×</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <label className="block"><span className="text-sm font-semibold text-gray-700">Article (tel qu’écrit sur le BL)</span>
            <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="LAVACH Sauce frites bio 200 ml" className={`mt-1 ${champ}`} autoFocus /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-sm font-semibold text-gray-700">Quantité reçue (pièces ou kg)</span>
              <input value={quantite} onChange={(e) => setQuantite(e.target.value)} inputMode="decimal" placeholder="12" className={`mt-1 ${champ}`} /></label>
            <label className="block"><span className="text-sm font-semibold text-gray-700">Prix d’achat net HTVA / unité</span>
              <input value={prix} onChange={(e) => setPrix(e.target.value)} inputMode="decimal" placeholder="2,116" className={`mt-1 ${champ}`} /></label>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-700">TVA</span>
            <div className="mt-1 flex gap-2">
              {[6, 21, 0, 12].map((t) => (
                <button key={t} type="button" onClick={() => setTva(t)}
                  className={`px-4 py-2 rounded-lg border-2 font-semibold ${tva === t ? 'border-emerald-700 bg-emerald-50 text-emerald-800' : 'border-gray-300 text-gray-700'}`}>
                  {t} %
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-sm font-semibold text-gray-700">Réf. fournisseur</span>
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="050711" className={`mt-1 ${champ}`} /></label>
            <label className="block"><span className="text-sm font-semibold text-gray-700">Code-barres (facultatif)</span>
              <input value={ean} onChange={(e) => setEan(e.target.value)} inputMode="numeric" className={`mt-1 ${champ}`} /></label>
          </div>
          <label className="block"><span className="text-sm font-semibold text-gray-700">DLC / DLUO (facultatif)</span>
            <input type="date" value={dluo} onChange={(e) => setDluo(e.target.value)} className={`mt-1 ${champ}`} /></label>
          <p className="text-sm text-gray-600">
            La ligne s’ajoute « en attente » : rattachez-la à une fiche (ou créez-la), puis validez-la comme les autres.
          </p>
        </div>
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-3 rounded-xl border">Annuler</button>
          <button onClick={ajouter} disabled={!valide || envoi} className="px-6 py-3 rounded-xl bg-emerald-700 text-white font-bold disabled:bg-gray-300">
            {envoi ? 'Ajout…' : 'Ajouter à la réception'}
          </button>
        </div>
      </div>
    </div>
  );
}
